'use client'

import React from 'react'
import Link from 'next/link'
import { Person } from '@/payload-types'
import Image from 'next/image'
import { isMedia } from '@/lib/utils'
import {
  Button,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  SheetPerson,
} from '@/components'
import { Mail, Phone } from 'lucide-react'

interface CardPersonProps {
  person: Person
}

const initials = (str: string) =>
  str
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')

function CardPerson({ person }: Readonly<CardPersonProps>): React.ReactNode {
  const photo = person.photo

  return (
    <div
      key={person.id}
      className="relative group flex flex-col rounded-xl border overflow-hidden hover:shadow-md transition-shadow"
    >
      <figure className="relative bg-muted aspect-square w-full flex justify-center items-center">
        {photo && isMedia(photo) ? (
          <Image src={photo.url!} alt={photo.alt || person.name} fill objectFit="cover" />
        ) : (
          <figcaption className="cursor-default flex justify-center items-center w-40 h-40 font-semibold text-2xl rounded-full bg-foreground/10">
            {initials(person.name)}
          </figcaption>
        )}
      </figure>
      <div className="p-3 flex flex-col gap-1">
        <h3 className="font-semibold text-base leading-tight">{person.name}</h3>
        {person.position ? (
          <p className="text-sm text-muted-foreground line-clamp-3">{person.position}</p>
        ) : null}
      </div>
      <footer className="flex flex-row justify-between mt-auto p-3">
        {person.email || person.phone ? (
          <ul className="flex flex-row gap-2">
            {person.email ? (
              <li>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="default" size="icon-sm" className="rounded-full" asChild>
                        <Link href={`mail:${person.email}`}>
                          <Mail strokeWidth={1} />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>E-mail</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </li>
            ) : null}
            {person.phone ? (
              <li>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="default" size="icon-sm" className="rounded-full" asChild>
                        <Link href={`tel:${person.phone}`}>
                          <Phone strokeWidth={1} />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Téléphone</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </li>
            ) : null}
          </ul>
        ) : null}
        <SheetPerson person={person} />
      </footer>
    </div>
  )
}

export { CardPerson, type CardPersonProps }
